import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  fileRecordVersionKey,
  pickFileRecordMediaURL,
  useStickyMediaSrc,
} from "@cohesive/ui-components";
import { useState } from "react";
import { LoadingOverlay } from "../LoadingOverlay";
import { getFileRecordName } from "../contentType";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

export function ImageContent({ record }: { record: FileRecord }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const fileName = getFileRecordName(record);
  const { previewMediaUrl } = useFileRecordVersionPreview();
  const mediaUrl =
    previewMediaUrl ??
    pickFileRecordMediaURL(record, "full") ??
    pickFileRecordMediaURL(record, "thumbnail");
  // The loaded link stays until it fails; a renewed one never re-downloads.
  const { src, onError } = useStickyMediaSrc(mediaUrl);

  if (!mediaUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Image not available.
      </div>
    );
  }

  return (
    <div className="flex h-full w-full min-h-0 flex-col bg-muted/20">
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto">
        <img
          key={previewMediaUrl ?? fileRecordVersionKey(record)}
          src={src}
          alt={fileName}
          className={cn(
            "max-h-full max-w-full object-contain transition-opacity duration-300",
            loaded && !errored ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setTimeout(() => setLoaded(true), 300)}
          onError={() => {
            if (!onError()) setErrored(true);
          }}
        />
        {!loaded && !errored && <LoadingOverlay />}
        {errored && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            Failed to load image.
          </div>
        )}
      </div>
      {record.generation_prompt && (
        <div className="shrink-0 border-t bg-background p-6 space-y-2 max-h-64 overflow-y-auto">
          <h3 className="text-sm font-medium text-foreground">Prompt</h3>
          <p className="text-sm text-muted-foreground italic leading-relaxed whitespace-pre-wrap select-text">
            &quot;{record.generation_prompt}&quot;
          </p>
        </div>
      )}
    </div>
  );
}
